import { JWMButton } from "@components/JWMButton/JWMButton";
import { JWMHtml } from "@components/JWMHtml/JWMHtml";
import { JWMLink } from "@components/JWMLink";
import { Button } from "@jmc/solid-design-system/src/components/atoms/Button/Button";
import { ContentAlignment } from "@jmc/solid-design-system/src/components/atoms/Hero/Hero";
import { Callout } from "@jmc/solid-design-system/src/components/molecules/Callout/Callout";
import { ImagePosition } from "@jmc/solid-design-system/src/components/molecules/Card/Card";
import { BreakPoint, useMediaQuery } from "@jmc/solid-design-system/src/hooks/useMediaQuery/useMediaQuery";
import classnames from "classnames";
import { GatsbyImage, IGatsbyImageData } from "gatsby-plugin-image";
import React from "react";

import style from "./style.module.scss";

interface Props {
    id: string;
    title?: string;
    backgroundColor?: "primary" | "grey" | "white";
    description?: string;
    image?: IGatsbyImageData;
    footer?: {
        buttonProps: React.ComponentProps<typeof Button>;
        link: { external?: boolean; url?: string; url_prefix?: string; title: string; anchor_id: string };
        text: string;
    }[];
    contentAlignment?: ContentAlignment;
    placement?: string;
    imagePosition?: ImagePosition;
    loading?: "eager" | "lazy";
    siteSpecialty?: {
        type: string;
        title: string;
    };
    showGlossary?: boolean;
    inFullWidthSection?: boolean;
}

export const JWMCallout = (props: Props): JSX.Element => {
    const {
        id,
        backgroundColor = "primary",
        contentAlignment,
        description,
        footer,
        inFullWidthSection,
        image,
        imagePosition,
        loading,
        placement,
        siteSpecialty,
        showGlossary,
        title,
    } = props;

    const isMobile = useMediaQuery(BreakPoint.md);

    const calloutFooter = footer?.map((button, index) => {
        if (button?.link?.title && button?.link?.url) {
            return index === 0 ? (
                <JWMButton
                    key={index}
                    buttonProps={{
                        color: backgroundColor === "primary" ? "white" : "primary",
                        variant: "contained",
                    }}
                    text={button?.text}
                    anchor_id={button?.link?.anchor_id}
                    url={button?.link?.url}
                    url_prefix={button?.link?.url_prefix}
                    fullWidth={false}
                    external={button?.link?.external}
                    placement={props?.placement}
                    size={isMobile ? "small" : "medium"}
                />
            ) : (
                <JWMLink
                    key={index}
                    anchor_id={button?.link?.anchor_id}
                    url={button?.link?.url}
                    url_prefix={button?.link?.url_prefix}
                    external={button?.link?.external}
                    placement={props?.placement}
                    fullWidth={false}
                >
                    {button?.text}
                </JWMLink>
            );
        }
    });

    // Limit image ratios to 4:3 and 3:4
    const imageRatio = Math.min(Math.max(image?.height / image?.width, 0.75), 1.33);

    const calloutImage = image?.images ? (
        <GatsbyImage
            image={image}
            alt={title || ""}
            loading={loading}
            style={{
                top: 0,
                insetInlineStart: 0,
                paddingBlockStart: `${imageRatio * 100}%`,
                height: 0,
            }}
            imgStyle={{ objectPosition: imagePosition?.replace("-", " ") }}
        />
    ) : typeof image === "string" ? (
        <img
            src={image}
            loading={"eager"}
            alt={title || ""}
            style={{
                position: "absolute",
                width: "100%",
                height: "100%",
                top: 0,
                insetInlineStart: 0,
                objectFit: "cover",
                objectPosition: imagePosition?.replace("-", " "),
            }}
        ></img>
    ) : null;

    const calloutTitle = (
        <JWMHtml
            placement={placement}
            siteSpecialty={siteSpecialty}
            showGlossary={showGlossary === undefined ? true : showGlossary}
        >
            {title}
        </JWMHtml>
    );

    return (
        <div className={classnames(style.wrapper, inFullWidthSection ? style.inFullWidthSection : null)}>
            <Callout
                id={id}
                backgroundColor={backgroundColor}
                contentAlignment={contentAlignment}
                image={calloutImage}
                imagePosition={imagePosition}
                loading={loading}
                footer={calloutFooter}
                title={calloutTitle}
            >
                {description ? (
                    <JWMHtml
                        color={"inherit"}
                        placement={placement}
                        siteSpecialty={siteSpecialty}
                        showGlossary={showGlossary === undefined ? true : showGlossary}
                    >
                        {description}
                    </JWMHtml>
                ) : null}
            </Callout>
        </div>
    );
};
export default JWMCallout;
